import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { ApolloError, gql } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import * as O from "fp-ts/Option";
import * as RA from "fp-ts/ReadonlyArray";
import { pipe } from "fp-ts/function";

import { Button } from "src/components/button/button";
import { Error } from "src/components/error/error";
import { Input } from "src/components/input/input";
import { FormikSelect } from "src/components/select/select";
import { Toggle } from "src/components/toggle/toggle";
import { SectionTitle } from "src/components/typography/typography";
import { CreateUserInput, CreateUserPayload, SchoolConnection, SchoolEdge } from "src/generated/types";
import { parseError } from "src/utils/parse_error";

import { AdminLayout } from "./admin_layout";
import { getSchoolChoices } from "./helpers";
import { ALL_SCHOOLS_QUERY } from "./queries";

/*
 * GraphQL
 */

const CREATE_USER_MUTATION = gql`
    mutation CreateUser($input: CreateUserInput!) {
        createUser(input: $input) {
            user {
                id
            }
        }
    }
`;

/*
 * Types
 */

interface ApolloMutationVars {
    input: CreateUserInput;
}

interface ApolloMutationData {
    createUser: CreateUserPayload;
}

interface ApolloQueryData {
    schools: SchoolConnection;
}

interface FormValues {
    email: string;
    password: string;
    passwordConfirm: string;
    schoolId: string;
}

/*
 * Main
 */

export const CreateUserPage = (): React.ReactElement => {
    const { data, error } = useQuery<ApolloQueryData, Empty>(ALL_SCHOOLS_QUERY, {
        fetchPolicy: "network-only",
    });
    const [createUser, { loading: submitting }] = useMutation<ApolloMutationData, ApolloMutationVars>(
        CREATE_USER_MUTATION,
    );
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const initialValues: FormValues = {
        email: "",
        password: "",
        passwordConfirm: "",
        schoolId: "",
    };

    if (error) return <Error />;

    return (
        <AdminLayout>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { resetForm, setSubmitting }): void => {
                    createUser({
                        variables: {
                            input: {
                                fields: {
                                    isAdmin,
                                    email: values.email,
                                    password: values.password,
                                    school: {
                                        link: values.schoolId,
                                    },
                                    username: values.email,
                                },
                            },
                        },
                    })
                        .then(() => {
                            toast.success("User created successfully");
                        })
                        .catch((e: ApolloError) => {
                            toast.error(parseError(e));
                        });

                    setSubmitting(false);
                    resetForm();
                }}
                validationSchema={Yup.object().shape({
                    email: Yup.string().required("Required"),
                    password: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
                    passwordConfirm: Yup.string()
                        .oneOf([Yup.ref("password"), null], "Passwords must match")
                        .required("Required"),
                    schoolId: isAdmin ? Yup.string() : Yup.string().required("Required"),
                })}
            >
                {(formik: FormikProps<FormValues>) => (
                    <Form>
                        <SectionTitle>Create a New User</SectionTitle>
                        <Toggle
                            checked={isAdmin}
                            label="ACSI Admin"
                            name="adminToggle"
                            onChange={(): void => {
                                setIsAdmin(!isAdmin);
                            }}
                        />
                        <div className="spacer" />
                        <Input
                            autoComplete="off"
                            id="email"
                            label="Email"
                            name="email"
                            placeholder="Enter Email Address"
                            type="email"
                        />
                        <Input
                            autoComplete="new-password"
                            id="password"
                            label="Password"
                            name="password"
                            placeholder="Enter Password"
                            type="password"
                        />
                        <Input
                            autoComplete="new-password"
                            id="passwordConfirm"
                            label="Confirm Password"
                            name="passwordConfirm"
                            placeholder="Confirm Password"
                            type="password"
                        />
                        <FormikSelect
                            disabled={isAdmin}
                            label="School"
                            name="schoolId"
                            options={pipe(
                                O.fromNullable(data?.schools.edges),
                                O.fold(
                                    () => RA.empty,
                                    (edges) => getSchoolChoices(edges as ReadonlyArray<SchoolEdge>),
                                ),
                            )}
                        />
                        <Button disabled={formik.isSubmitting || !formik.isValid || submitting} type="submit">
                            Submit
                        </Button>
                    </Form>
                )}
            </Formik>
        </AdminLayout>
    );
};
